import * as React from "react";
import { useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import { DialogContent, Dialog, DialogTrigger, DialogSurface, DialogTitle, DialogBody, Button, DialogActions, Field, Checkbox, Label, Textarea, TextareaOnChangeData } from "@fluentui/react-components";
import { Spinner } from '@fluentui/react-components';
import { CheckmarkCircle24Regular } from "@fluentui/react-icons";
import { Separator } from "@fluentui/react";
import ContractConceptSelectorComponent from "../../../commons/component/Inputs/ContractConceptSelector/ContractConceptSelector.Component";
import { ContractConcept } from "../../../models/Entities/Contracts/ContractConcept";

// component functions
import * as Functions from "./VisitConceptLeafModal.Functions";
import { connect } from "react-redux";
import ProviderSelectorComponent from "../../../commons/component/Inputs/ProviderSelector/ProviderSelectorComponent";

// models
import { ContractConceptRelationship } from "../../../models/Entities/Contracts/ContractConceptRelationship";
import { AvailableMedicalService } from "../../../models/Entities/MedicalServices/AvailableMedicalService";

// components
import MedicalServiceSelectorComponent from "../../../commons/component/Inputs/MedicalServiceSelector/MedicalServiceSelectorComponent";
import { Contract } from "../../../redux/actions";


const VisitConceptLeafModal = (props: Functions.Props) => {
  const { t } = useTranslation();

  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // propesties for ContractConceptRelationship

  const [contractConceptId, setContractConceptId] = useState<number | undefined>(undefined);
  const [formConcept, setFormConcept] = useState<string | undefined>(undefined);
  const [fromConceptNameToShow, setFormConceptNameToShow] = useState<string | undefined>(undefined);
  const [isPayable, setIsPayable] = useState(false);
  const [medicalServiceId, setMedicalServiceId] = useState<number | undefined>(undefined);
  const [providerId, setProviderId] = useState<string | undefined>(undefined);
  const [itemNumber, setItemNumber] = useState<number | undefined>(undefined);
  const [formName, setFormName] = useState<string | undefined>(undefined);
  const [isProcedure, setIsProcedure] = useState<boolean>(false);

  // events
  const handleSubmit = () => {
    //alert('Submitting form');
    // if (!contractConceptId) {
    //   setErrorMsg(t('visitConceptForm.errors.selectContractConcept', 'You must select a contract concept'));
    //   return;
    // }
    setErrorMsg(undefined);
    if (props.CurrentConceptRelationship && props.CurrentConceptRelationship.length > 0) {
      //alert('Updating existing relationship');
      props.updateContractConceptRelationship(
        {
          contractId: props.ContractId,
          contractConceptId: isPayable ? contractConceptId! : undefined,
          formName: formConcept === t('visitConceptLeafModal.realizedLabel', 'Realized') ? formName! : undefined,
          itemNumber: formConcept === t('visitConceptLeafModal.realizedLabel', 'Realized') ? itemNumber! : undefined,
          formConcept: formConcept!,
          formConceptNameToShow: fromConceptNameToShow ? fromConceptNameToShow : formConcept!,
          isPayable: isPayable,
          isProcedure: isProcedure,
          medicalServiceId: medicalServiceId!,
          providerId: providerId!
        } as ContractConceptRelationship
      )
    } else {
      //alert('Creating new relationship');
      props.createContractConceptRelationship(
        props.ContractId,
        {
          contractId: props.ContractId,
          contractConceptId: isPayable ? contractConceptId! : undefined,
          itemNumber: itemNumber!,
          formName: formName!,
          formConcept: formConcept!,
          formConceptNameToShow: fromConceptNameToShow ? fromConceptNameToShow : formConcept!,
          isPayable: isPayable,
          isProcedure: isProcedure,
          medicalServiceId: medicalServiceId!,
          providerId: providerId!,
        }
      );
    }
    setSubmitted(true);
  };

  const onConceptSelected = (concepts: ContractConcept[]) => {
    if (concepts && concepts.length > 0) {
      setContractConceptId(concepts[0].id);
    } else {
      setContractConceptId(undefined);
    }
  };

  React.useEffect(() => {
    if (open && submitted && (props.successAdding || props.isUpdatedSuccessfully)) {
      setOpen(false);
      setSubmitted(false);
    }
  }, [props.isCreatedSuccessfully, props.isUpdatedSuccessfully, submitted]);

  React.useEffect(() => {
    setFormConcept(props.label);
  }, [props.label]);


  // Find existing relationship for this form concept (case-insensitive via normalized value)
  const normalizedFormConcept = useMemo(() => (props.label ?? '').toString().toUpperCase(), [props.label]);
  const existingRelationship = useMemo(() => {
    return props.ConceptRelationships?.find(r => r.formConcept.toUpperCase() === normalizedFormConcept);
  }, [props.ConceptRelationships, normalizedFormConcept]);

  // Initialize fields from existing relationship when present
  React.useEffect(() => {
    if (existingRelationship) {
      setIsPayable(!!existingRelationship.isPayable);
      setContractConceptId(existingRelationship.contractConceptId);
      setIsProcedure(!!existingRelationship.isProcedure);
      setProviderId(existingRelationship.providerId ?? undefined);
      setMedicalServiceId(existingRelationship.medicalServiceId ?? undefined);
      setItemNumber(existingRelationship.itemNumber ?? undefined);
      setFormName(existingRelationship.formName ?? undefined);
      setFormConceptNameToShow(existingRelationship.formConceptNameToShow ?? undefined);
    }
  }, [existingRelationship]);

  const FormFields = (
    <>
      <Field>
        <Checkbox
          label={t('visitConceptLeafModal.isPayable', 'Is payable')}
          checked={isPayable}
          onChange={(_, data) => setIsPayable(!!data.checked)}
        />
      </Field>
      {isPayable && (
        <>
          <Field validationState={errorMsg ? 'error' : undefined} validationMessage={errorMsg}>
            <ContractConceptSelectorComponent
              ContractId={props.ContractId}
              referralDate={undefined}
              onSelectedContractConcept={
                (concepts: ContractConcept[]) => onConceptSelected(concepts)
              }
              ContractConceptsSelected={
                contractConceptId ? [contractConceptId] : undefined
              }
              MultiselectEnable={false} />
          </Field>
          <Field>
            <Label>{t('visitConceptLeafModal.formConceptNameToShow', 'Form concept name to show')}</Label>
            <Textarea 
              value={fromConceptNameToShow || formConcept}
              onChange={(ev: React.ChangeEvent<HTMLTextAreaElement>, data: TextareaOnChangeData) => setFormConceptNameToShow(data.value)}
              style={{ width: '100%' }}
            />
          </Field>
        </>)}
      <Field>
        <Checkbox
          label={t('visitConceptLeafModal.isProcedure', 'Is procedure')}
          checked={isProcedure}
          onChange={(_, data) => setIsProcedure(!!data.checked)}
        />
      </Field>
      {isProcedure && (
        <>
          <Field>
            <MedicalServiceSelectorComponent
              MultiselectEnable={false}
              MedicalServicesSelectedIds={medicalServiceId ? [medicalServiceId] : undefined}
              onSelectedServices={(services: AvailableMedicalService[] | undefined) => setMedicalServiceId(services && services.length > 0 ? services[0]?.id : undefined)}
              noteText={''}
            />
          </Field>
          <Field>
            <ProviderSelectorComponent
              MultiselectEnable={false}
              ProviderSelected={providerId ? [providerId] : undefined}
              onChangeSelection={(providers) => {
                const firstProviderId = providers && providers.length > 0 ? providers[0]?.person.personIdentifier : undefined;
                setProviderId(firstProviderId ? String(firstProviderId) : undefined);
              }}
            />
          </Field>
        </>
      )}
    </>
  );

  return (
    <Dialog modalType="alert" open={open} onOpenChange={(event, data) => setOpen(data.open)}>
      <DialogTrigger disableButtonEnhancement>
        {(React.isValidElement(props.triggerAs) && props.triggerAs) || (
          <Button size="small" appearance="subtle">
            {t('visitConceptLeafModal.openForm', 'Open form')}
          </Button>
        )}
      </DialogTrigger>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t('visitConceptLeafModal.title', 'Asociar concepto')}</DialogTitle>
          <DialogContent>
            { (props.label === t('visitConceptLeafModal.realizedLabel', 'Realized')) && <>
              <Label>{'Formulario:' + props.formName} {' > '} {'item: ' + props.itemNumber}</Label>
              <Separator />
            </>}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexDirection: 'row' }}>
              {props.realized ? (
                <CheckmarkCircle24Regular
                  color="green"
                  title={t('visitConceptLeafModal.realizedLabel', 'Realized')}
                />
              ) : null}
              <strong>{props.label}</strong>
            </div>
            <Separator />
            {FormFields}
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" onClick={handleSubmit} disabled={props.isCreating || props.isUpdating} icon={(props.isCreating || props.isUpdating) ? <Spinner size="extra-tiny" /> : undefined}>
              {props.isAdding ? t('common.saving') : t('visitConceptLeafModal.confirm', 'Confirm')}
            </Button>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary" onClick={() => setOpen(false)}>{(props as any)?.t?.('common.cancel') ?? 'Cancel'}</Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default connect(
  Functions.mapStateToProps,
  Functions.mapDispatchToProps
)(VisitConceptLeafModal as any);
